import { SearchResult, SuggestResult } from 'core/interfaces/dictionary-interfaces'

import { ThaiWordFullProps, ThaiWordLookupProps } from 'core/interfaces/dictionary-thai-interfaces'
import { ApiWordObject, WordTranslation } from '../lib/interfaces'
import { isThaiText } from '../lib/generic-utils/strings'
import { apiVersion } from 'core/config'
import { getSessionToken, getSessionTokenIfReady, clearSessionToken } from './turnstile-session'
import { deployTokenHeader } from './deploy-token'
import { authHeader } from './auth-token'
import { reloadIfStaleBundle } from 'lib/stale-bundle-reload'
import { requestSignIn, SignInRequiredMessage, TranslationBusyMessage } from './auth-gate'
import {
   mergeVettedCompounds,
   segmentUnknownWord,
   SegmentationLexiconEntry,
} from '../lib/thai-segmentation'
import segmentationWordlist from '../data/segmentation-wordlist.json'
import compoundMergeList from '../data/compound-merge-list.json'
import { guessLoanwordEnglish } from '../lib/loanword-guess'
import { narrowMeaningsToSenseGroup } from '../lib/sense-groups'
import loanwordIndex from '../data/loanword-index.json'
import { segmentSyllables } from 'nlp'
import {
   findSeparableMatches,
   groupSeparableTokens,
   SeparablePattern,
} from '../lib/separable-matching'
import separableVerbsData from '../data/separable-verbs.json'
import {
   detectDominantLanguage,
   directionKeyFor,
   type DirectionKey,
} from '../../shared/language-direction'
import type {
   ThaiWordLookup,
   TranslateEntry,
   TranslateResponseV2,
} from '../../shared/translate-contract'
import type { EngWordLookupDetail } from '../../shared/dictionary-contract'
import { computeTrailingSpaces } from '../lib/align-spacing'
import { splitQueryIntoLines } from '../lib/splitSentences'

// Word → { phonetic, meanings, sound } map generated by
// scripts/generate-segmentation-wordlist.js
const segmentationLexicon: Record<string, SegmentationLexiconEntry> = segmentationWordlist

// Separable resultative verbs (ลองดู ↔ ลอง … ดู) generated by
// scripts/separable-verbs/build-separables.js
const separableVerbs: SeparablePattern[] = separableVerbsData

// Pipeline-vetted Thai compounds (scripts/compound-segmentation/build-merge-list.js)
// used to re-merge the translate worker's over-split spacing (พวก เขา → พวกเขา)
// before the dictionary lookup, so a compound renders on one token.
const compoundMergeSet = new Set<string>(compoundMergeList)
const maxCompoundLength = compoundMergeList.reduce((max, word) => Math.max(max, word.length), 0)
const mergeCompounds = (spaced: string): string =>
   mergeVettedCompounds(spaced, compoundMergeSet, maxCompoundLength)

// Every unique word across the spaced lines, for one batch dictionary lookup.
// Lines containing a separable verb split into its parts (ลอง … ดู) also look
// up the canonical entry (ลองดู) so the grouped meaning is available.
const collectLookupTokens = (spacedLines: string[]): string[] =>
   spacedLines.flatMap((line) => {
      const tokens = line.split(' ').filter((w) => w.trim().length > 0)
      return [...tokens, ...findSeparableMatches(tokens, separableVerbs).map((m) => m.word)]
   })

// Strip surrounding punctuation (quotes, brackets etc.) so tokens like
// '\u0E2A\u0E27\u0E31\u0E2A\u0E14\u0E35' still match their dictionary entry.
function normalizeThaiToken(word: string): string {
   return word.replace(/^[^\u0E00-\u0E7Fa-zA-Z0-9]+|[^\u0E00-\u0E7Fa-zA-Z0-9]+$/g, '')
}

const isDev = import.meta.env.DEV
const isServer = typeof window === 'undefined'

// Where the two API workers are, and why the browser and a server render do not
// agree about it.
//
// In the browser both are same-origin path mounts on the site's own hostname:
// thai2english.com/api/dictionary/* and /api/translate/* are Worker routes that
// run ahead of this app's own worker. Same-origin means no CORS preflight on any
// of these calls — the dictionary sends X-T2E-Client and Authorization and
// translate posts JSON, so none of them were ever CORS-simple — and one
// connection instead of two. Dev proxies the same two paths onto the local
// `wrangler dev` ports; see vite.config.ts.
//
// Server-side, neither of those URLs works. A relative URL has no origin to
// resolve against, and — the part that is easy to get wrong — a Worker
// subrequest to a URL on its own zone is routed to that zone's origin with any
// Workers mapped to it ignored, so an SSR fetch to
// thai2english.com/api/dictionary/* would land back in this same worker and 404
// out of src/routes/api/$.ts. SSR therefore talks to each worker's own Custom
// Domain, where the worker *is* the origin. Full reasoning in
// ai/docs/api-routing.md.
//
// The dictionary's primary SSR path is the DICTIONARY service binding
// (viaDictionaryBinding below); this is only its fallback. Translate is never
// reached under SSR at all — processQuery runs from a click handler, and the two
// entry-page loaders take getTranslations' spacedQueries branch, which is
// dictionary-only — so its server branch is a guard, not a live path.
const dictionaryHost = 'https://t2e-dictionary-prod.thai2english.com'
const translateHost = 'https://t2e-translate-prod.thai2english.com'

// Exported so the tests key their fetch stubs off the same constants the code
// uses, rather than literals that silently stop matching when these change.
export const dictionaryBaseUrl = isServer
   ? isDev
      ? 'http://localhost:8788'
      : dictionaryHost
   : '/api/dictionary'

export const translateBaseUrl = isServer
   ? isDev
      ? 'http://localhost:8787'
      : translateHost
   : '/api/translate'

// Browsers attach Origin themselves on a cross-origin fetch; a server-side one
// has to state it. Still needed after the move to same-origin paths: the server
// branch above talks to the Custom Domain, which is cross-origin and still
// refuses a request with no Origin. The value has to be on the worker's
// ALLOWED_ORIGINS for the environment — production's wrangler.jsonc, dev's
// .dev.vars.
const serverOriginHeader: Record<string, string> = isServer
   ? { Origin: isDev ? 'http://localhost:3000' : 'https://www.thai2english.com' }
   : {}

// True only inside workerd. `import.meta.env.SSR` alone is not enough to tell a
// real server render from a vitest run — vitest also transforms in SSR mode —
// and only workerd can resolve `cloudflare:workers`. Checking the runtime keeps
// the dynamic import below from being attempted anywhere it cannot succeed.
const isWorkerd = (): boolean =>
   typeof navigator !== 'undefined' && navigator.userAgent === 'Cloudflare-Workers'

// Read the dictionary over the service binding when server-rendering, so an
// entry page costs one billed request instead of two and skips a public round
// trip. `import.meta.env.SSR` is statically false in the client build, so this
// and its import of `cloudflare:workers` are dropped from the browser bundle.
//
// Returns null whenever the binding isn't there — every caller falls back to
// the HTTP path below, which is what keeps dev, the test suite and a
// binding-less deploy working unchanged.
async function viaDictionaryBinding<T>(
   use: (rpc: typeof import('../server/dictionary-rpc')) => Promise<T | null>
): Promise<T | null> {
   if (!import.meta.env.SSR || !isWorkerd()) return null
   try {
      return await use(await import('../server/dictionary-rpc'))
   } catch (err) {
      console.error('[dictionary-rpc] binding unavailable, falling back to fetch', err)
      return null
   }
}

// What one translate call yields, and what the client-side cache stores.
// `thaiWordLookups` rides along with the translation when the worker could
// reach the dictionary; when it is absent the client looks the words up itself.
type WorkerCallResult = {
   entries: TranslateEntry[]
   model?: string
   thaiWordLookups?: ThaiWordLookup[]
}

export interface WorkerTranslationResult {
   englishTranslation: string
   wordTranslation: WordTranslation
   // True when the worker could not translate this line: wordTranslation then
   // carries the user's own text with no word data, and the UI renders it
   // with a "couldn't translate" state instead of silently degrading.
   translationFailed: boolean
   // True when the translate worker itself was unreachable (rate limit,
   // outage, timeout) and the line was segmented locally instead: the word
   // data is real, but there is no English translation to show.
   translationUnavailable?: boolean
   model?: string
}

// Owner-only debugging: when this localStorage flag is set, ask the worker to
// reveal which model produced the translation. Enable in the browser console:
//    localStorage.setItem('t2e-debug', '1')
function isDebugEnabled(): boolean {
   try {
      return localStorage.getItem('t2e-debug') === '1'
   } catch {
      return false
   }
}

// Failures from the dictionary worker carry their status so callers can tell a
// transient error from a refusal. The deploy-token gate answers 403, and a
// refused request stays refused however many times it is retried — see the
// fallback in lookupThaiWords.
type DictionaryError = Error & { response: { status: number; body: Record<string, unknown> } }

async function dictionaryError(response: Response): Promise<DictionaryError> {
   const body = (await response.json().catch(() => ({}))) as Record<string, unknown>
   const error = new Error(`Dictionary API returned ${response.status}`) as DictionaryError
   error.response = { status: response.status, body }
   return error
}

// What the suggest panel falls back to: no matches, no exact hit. Built fresh
// each time so a caller can't mutate a shared instance.
const emptySuggestResult = (): SuggestResult => ({
   isThai: false,
   exactMatch: null,
   exactLang: null,
   thaiMatches: [],
   engMatches: [],
   translitMatches: [],
})

function isRefusal(err: unknown): boolean {
   return (err as DictionaryError | undefined)?.response?.status === 403
}

/**
 * Reloads if this refusal is one a reload fixes, reporting whether it did.
 *
 * A tab on a bundle the workers no longer accept is refused on every request
 * and has no reason to reload on its own — searching never leaves the route, so
 * stale-chunk-reload.ts never fires. This is where that tab gets rescued
 * instead. See src/lib/stale-bundle-reload.ts for which reasons qualify.
 */
function reloadedForRefusal(err: unknown): boolean {
   return reloadIfStaleBundle((err as DictionaryError | undefined)?.response?.body)
}

/**
 * Returned when a reload is under way: the page is being replaced, and a
 * promise that settles first would let the caller paint an error or a sign-in
 * screen in the moment before it goes.
 */
function pageIsReloading<T>(): Promise<T> {
   return new Promise<T>(() => {})
}

/**
 * Which sign-in wording a refused translation deserves.
 *
 * The translate worker refuses with 403 for two different reasons, and they
 * call for opposite advice: the deploy-token gate ('client_required') is
 * something reloading and waiting will never fix, while the login gate
 * ('login_required') is a capacity pause that lifts on its own. Anything else —
 * an unreadable body, an older worker that sends no code — gets the
 * deploy-token wording, which is the broader case.
 */
function refusalMessage(body: unknown): string {
   if ((body as { error?: unknown } | null | undefined)?.error === 'login_required') {
      return TranslationBusyMessage
   }
   return SignInRequiredMessage
}

class Thai2EnglishApi {
   workerUrl = translateBaseUrl
   cfUrl = dictionaryBaseUrl

   timeoutConfig = 16000

   private workerCache = new Map<string, WorkerCallResult>()
   private readonly WORKER_CACHE_MAX = 200

   // Keyed by the clipped query. Holds the *promise*, not the result, so two
   // callers that start the same search before either has resolved share one
   // request — which is what the `?q=` page does, since its effect can re-run
   // before the first run's fetch comes back.
   // The suggest tier's equivalent, and it can afford to be much bigger: a
   // suggest response is a couple of KB, and every prefix of every word typed
   // this session lands here. Backspacing through a word then costs no requests
   // at all — which is the single most common thing a typist does.
   private suggestCache = new Map<string, Promise<SuggestResult>>()
   private readonly SUGGEST_CACHE_MAX = 150

   private searchCache = new Map<string, Promise<SearchResult>>()
   // Deliberately smaller than WORKER_CACHE_MAX: a common entry's search
   // payload is ~40KB of JSON, and this exists to kill the immediate duplicate
   // and back/forward, not to archive a whole session.
   private readonly SEARCH_CACHE_MAX = 50

   // `retryAfterSignIn` guards the one retry a refusal is allowed: a second
   // refusal means signing in didn't help, and looping would just reopen the
   // sign-in screen forever.
   // `signal` lets a caller cancel a request that is no longer wanted — the
   // suggest path supersedes its own in-flight request on every keystroke. It is
   // combined with, not a replacement for, the timeout below.
   async getJson(path: string, retryAfterSignIn = true, signal?: AbortSignal) {
      const url = `${this.cfUrl}${path}`

      try {
         const controller = new AbortController()
         // Abort if the caller's signal fires, including when it has already
         // fired before we got here.
         const onExternalAbort = () => controller.abort()
         if (signal) {
            if (signal.aborted) controller.abort()
            else signal.addEventListener('abort', onExternalAbort, { once: true })
         }

         const options: RequestInit = {
            signal: controller.signal,
            // The auth header is what lets a client whose X-T2E-Client was
            // stripped or aged out still be served (see the worker's
            // enforceDeployToken). Empty when signed out.
            headers: { ...deployTokenHeader, ...serverOriginHeader, ...(await authHeader()) },
         }
         const timeoutId = setTimeout(() => controller.abort(), this.timeoutConfig)
         try {
            const response = await fetch(url, options).then(async (r) => {
               if (r.status >= 400) {
                  throw await dictionaryError(r)
               }
               return r.json()
            })

            return response
         } finally {
            clearTimeout(timeoutId)
            signal?.removeEventListener('abort', onExternalAbort)
         }
      } catch (err) {
         if (retryAfterSignIn && isRefusal(err)) {
            if (reloadedForRefusal(err)) return await pageIsReloading()
            if (await requestSignIn()) {
               return await this.getJson(path, false, signal)
            }
         }

         const errResponse = err.response || {}
         const { status } = errResponse
         const responseData = errResponse.body || {}

         if (status === 400 && responseData.errorType === 'WordDoesNotExist') {
            return { wordDoesNotExist: true }
         }
         if (status === 400 && responseData.errorType === 'EmptySearch') {
            return { emptySearch: true }
         }

         throw err
      }
   }

   async postJson(path: string, data: {}, retryAfterSignIn = true) {
      const url = `${this.cfUrl}${path}`

      const controller = new AbortController()
      const options: RequestInit = {
         signal: controller.signal,
         method: 'POST',
         body: JSON.stringify(data),
         headers: {
            'Content-Type': 'application/json',
            ...deployTokenHeader,
            ...serverOriginHeader,
            ...(await authHeader()),
         },
      }
      const timeoutId = setTimeout(() => controller.abort(), this.timeoutConfig)
      try {
         const response = await fetch(url, options).then(async (r) => {
            if (r.status >= 400) {
               throw await dictionaryError(r)
            }
            return r.json()
         })
         return response
      } catch (err) {
         if (retryAfterSignIn && isRefusal(err)) {
            if (reloadedForRefusal(err)) return await pageIsReloading()
            if (await requestSignIn()) {
               return await this.postJson(path, data, false)
            }
         }
         throw err
      } finally {
         clearTimeout(timeoutId)
      }
   }

   /**
    * Call the CF Worker to get AI-powered word spacing and English translations.
    * Sends the v2 contract (one entry back per line sent, self-describing) —
    * see shared/translate-contract.ts.
    */
   async callWorker(lines: string[], direction: DirectionKey): Promise<WorkerCallResult> {
      const cacheKey = `${direction}\n${lines.join('\n')}`
      const cached = this.workerCache.get(cacheKey)
      if (cached) return cached

      // Send the session header only if a token is already in hand. Waiting for
      // the Turnstile handshake here put a full challenge round trip in front of
      // every first translation for no gain — the worker treats the header as
      // advisory in 'log' mode, and the 401 path below still gets a real token
      // if enforcement is turned on.
      let sessionToken = getSessionTokenIfReady(this.workerUrl)

      const postTranslate = async (session: string | undefined) => {
         const controller = new AbortController()
         const timeoutId = setTimeout(() => controller.abort(), this.timeoutConfig)
         // Identifies the signed-in user to the worker. The worker verifies the
         // token itself, so the uid it records can't be spoofed; signed-out
         // visitors simply send nothing.
         const auth = await authHeader()
         try {
            return await fetch(this.workerUrl, {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                  ...deployTokenHeader,
                  ...auth,
                  ...(session ? { 'X-T2E-Session': session } : {}),
                  ...(isDebugEnabled() ? { 'X-T2E-Debug': '1' } : {}),
               },
               body: JSON.stringify({ v: 2, direction, lines }),
               signal: controller.signal,
            })
         } finally {
            clearTimeout(timeoutId)
         }
      }

      let response = await postTranslate(sessionToken)

      // Refused for want of a valid deploy token, or because the free
      // translation capacity is being held for signed-in users. Signing in is
      // the way through either way, so ask — with the wording that matches the
      // reason — then send the same request once more carrying the ID token. A
      // second 403 falls through to the error below.
      if (response.status === 403) {
         // Read once: the body decides both whether a reload can fix this and,
         // if not, which wording the sign-in screen should carry.
         const body: unknown = await response.json().catch(() => ({}))
         if (reloadIfStaleBundle(body)) return await pageIsReloading()
         if (await requestSignIn(refusalMessage(body))) {
            response = await postTranslate(sessionToken)
         }
      }

      if (response.status === 401) {
         // Session token expired or was rejected: re-run Turnstile once.
         clearSessionToken()
         try {
            sessionToken = await getSessionToken(this.workerUrl)
            response = await postTranslate(sessionToken)
         } catch {
            // Keep the original 401 response; the error below reports it.
         }
      }

      if (!response.ok) {
         const error = new Error(`Worker API returned ${response.status}`) as Error & {
            status: number
         }
         error.status = response.status
         throw error
      }

      const data = (await response.json()) as TranslateResponseV2
      if (!Array.isArray(data.entries) || data.entries.length !== lines.length) {
         throw new Error(
            `Worker returned ${data.entries?.length ?? 0} entries for ${lines.length} lines`
         )
      }

      const result: WorkerCallResult = {
         entries: data.entries,
         model: data.model,
         thaiWordLookups: data.thaiWordLookups,
      }
      // Browser only. This module is a singleton, and under SSR a Worker
      // isolate's module scope is shared by every request it handles, so
      // caching there would hand one visitor's translations to the next.
      if (!isServer) {
         if (this.workerCache.size >= this.WORKER_CACHE_MAX) {
            const key = this.workerCache.keys().next().value
            if (key) this.workerCache.delete(key)
         }
         this.workerCache.set(cacheKey, result)
      }
      return result
   }

   /**
    * Look up a batch of Thai words in the dictionary to get phonetics and meanings.
    * Uses the /search/thai-words POST endpoint for bulk lookup.
    */
   async lookupThaiWords(words: string[]): Promise<Map<string, ThaiWordLookupProps>> {
      const uniqueWords = [...new Set(words.map(normalizeThaiToken).filter((w) => w.length > 0))]
      if (!uniqueWords.length) return new Map()

      const wordMap = new Map<string, ThaiWordLookupProps>()

      // Over the binding the whole token set goes in one call — the entrypoint
      // chunks it server-side — and the result is the bare array the HTTP
      // endpoint wraps as `{ thaiMatches }`.
      const viaBinding = await viaDictionaryBinding((rpc) => rpc.lookupViaBinding(uniqueWords))
      if (viaBinding) {
         for (const match of viaBinding) {
            if (match.word) wordMap.set(match.word, match)
         }
         return wordMap
      }

      const BATCH_SIZE = 50
      for (let i = 0; i < uniqueWords.length; i += BATCH_SIZE) {
         const batch = uniqueWords.slice(i, i + BATCH_SIZE)
         try {
            const result = await this.postJson(`/search/${apiVersion}/thai-words`, {
               thaiWords: batch,
            })
            if (result?.thaiMatches) {
               for (const match of result.thaiMatches) {
                  if (match.word) wordMap.set(match.word, match)
               }
            }
         } catch (err) {
            // A refusal (deploy-token gate) would answer the same way to each of
            // the up-to-50 single-word retries below, so the fallback would turn
            // one rejected page into fifty. Only retry what might succeed.
            if (isRefusal(err)) {
               break
            }
            const results = await Promise.allSettled(
               batch.map((word) => this.fetchThaiDictionaryWord(word))
            )
            results.forEach((res, idx) => {
               if (res.status === 'fulfilled' && res.value) {
                  wordMap.set(batch[idx], res.value)
               }
            })
         }
      }

      return wordMap
   }

   /**
    * The English mirror of lookupThaiWords, for hydrating saved words.
    *
    * Three deliberate differences from the Thai path:
    *
    *   - No per-word retry fallback. There is no cheap single-word English
    *     endpoint — only the full entry with its example sentences — so firing
    *     50 of those would cost far more than the miss. A failed batch is
    *     skipped, and those rows keep rendering from their stored snapshot,
    *     which is exactly what the snapshot column is for.
    *   - A 404 from the endpoint resolves to an empty map rather than throwing,
    *     so a deploy that lands this before the dictionary worker degrades to
    *     snapshot-only rows instead of an error.
    *   - No token normalisation: an English headword is stored exactly as the
    *     dictionary spells it, and folding case here would stop 'Bangkok'
    *     matching itself.
    */
   async lookupEngWords(words: string[]): Promise<Map<string, EngWordLookupDetail>> {
      const uniqueWords = [...new Set(words.filter((word) => word.length > 0))]
      if (!uniqueWords.length) return new Map()

      const wordMap = new Map<string, EngWordLookupDetail>()

      const viaBinding = await viaDictionaryBinding((rpc) => rpc.lookupEngViaBinding(uniqueWords))
      if (viaBinding) {
         for (const match of viaBinding) {
            if (match.word) wordMap.set(match.word, match)
         }
         return wordMap
      }

      const BATCH_SIZE = 50
      for (let i = 0; i < uniqueWords.length; i += BATCH_SIZE) {
         const batch = uniqueWords.slice(i, i + BATCH_SIZE)
         try {
            const result = await this.postJson(`/search/${apiVersion}/eng-words`, {
               engWords: batch,
            })
            // Matched by `match.word`, never by index: the worker omits words
            // it does not have, so the response is shorter than the request and
            // positional alignment would silently mislabel every row after the
            // first miss.
            if (result?.engMatches) {
               for (const match of result.engMatches) {
                  if (match.word) wordMap.set(match.word, match)
               }
            }
         } catch (err) {
            // A refusal would answer every remaining batch the same way.
            if (isRefusal(err)) break
            // Anything else — including the 404 from a dictionary worker that
            // predates this endpoint — leaves these rows on their snapshot.
            console.error('[dictionary] eng-words lookup failed', err)
         }
      }

      return wordMap
   }

   /**
    * Build a WordTranslation from spaced Thai text and dictionary data.
    * Creates word objects with phonetics and meanings from dictionary lookups.
    */
   buildWordTranslation(
      originalText: string,
      spacedText: string,
      wordDictionary: Map<string, ThaiWordLookupProps>,
      naturalText?: string,
      // Thai token → the one sense id this sentence uses. When present for a
      // token, its tooltip shows only that sense instead of all of them.
      senseByWord?: Record<string, number>
   ): WordTranslation {
      const spacedWords = spacedText.split(' ').filter((w) => w.trim().length > 0)

      const alignText = naturalText || originalText
      const trailingSpaces = computeTrailingSpaces(alignText, spacedWords)

      const wordObjects: ApiWordObject[] = spacedWords.flatMap((word, wordIndex) => {
         // The dictionary is keyed by normalized tokens (see lookupThaiWords)
         const dictEntry = wordDictionary.get(word) ?? wordDictionary.get(normalizeThaiToken(word))

         const hasTrailingSpace = trailingSpaces[wordIndex] ?? false

         if (dictEntry) {
            const phonetic = dictEntry.t2e || ''
            // Meanings are carried raw (including "<i> (transliteration) </i>"
            // markers); transliteration is applied locally at render time.
            // Some entries (particles like ครับ) have no meaning text, only an
            // explanation — use it so the hover tooltip isn't empty.
            // Narrow to the sense this sentence uses, when the server told us
            // which (senseByWord) — widened to the sense's whole linked group
            // (oil ↔ oily), and falling back to all senses if the id isn't
            // among the lookup's meanings, so the tooltip is never emptied.
            const senseId = senseByWord?.[word] ?? senseByWord?.[normalizeThaiToken(word)]
            const dictMeanings = narrowMeaningsToSenseGroup(dictEntry.meanings || [], senseId)
            const meanings = dictMeanings.map((m) => ({
               meaning: m.meaning || m.explanation || '',
            }))

            return {
               word,
               phonetic,
               meanings,
               // Lookup entries don't carry a sound flag; the bundled lexicon does.
               hasSound: segmentationLexicon[normalizeThaiToken(word)]?.s === 1,
               isThai: true,
               commonnessScore: dictEntry.commonnessScore,
               hasTrailingSpace,
            }
         }

         // Word not found in dictionary — try to split it into known sub-words
         // (e.g. นะครับ → นะ + ครับ) so each still gets a transliteration.
         const subWords = segmentUnknownWord(normalizeThaiToken(word), segmentationLexicon)
         if (subWords) {
            return subWords.map((subWord, subIndex): ApiWordObject => {
               // A sub-word may already have full dictionary data if it was
               // looked up as its own token elsewhere in the text; otherwise
               // fall back to the bundled lexicon entry.
               const subEntry = wordDictionary.get(subWord)
               const lexEntry = segmentationLexicon[subWord]
               return {
                  word: subWord,
                  phonetic: subEntry?.t2e || lexEntry?.p || '',
                  meanings:
                     subEntry?.meanings?.map((m) => ({
                        meaning: m.meaning || m.explanation || '',
                     })) ||
                     lexEntry?.m.map((meaning) => ({ meaning })) ||
                     [],
                  hasSound: lexEntry?.s === 1,
                  isThai: true,
                  commonnessScore: subEntry?.commonnessScore,
                  hasTrailingSpace: subIndex === subWords.length - 1 ? hasTrailingSpace : false,
               }
            })
         }

         // No dictionary entry and no split. If it's Thai (e.g. an unlisted
         // loanword like อัปเดต / เรียลไทม์), fall back to segmenting it into
         // bare syllables so it still gets a transliteration — the respelling
         // pipeline reads raw Thai syllables the same as a dictionary t2e.
         // Loanwords transcribe an English word's spelling, so we can often
         // also guess the English source ("real time") as a marked meaning.
         const isThai = isThaiText(word)
         const syllables = isThai ? segmentSyllables(word) : undefined
         const guess = syllables ? guessLoanwordEnglish(syllables, loanwordIndex) : null

         return {
            word,
            phonetic: syllables ? syllables.join('-') : '',
            meanings: guess ? [{ meaning: guess, guessed: true }] : [],
            hasSound: false,
            isThai,
            hasTrailingSpace,
         }
      })

      // Build phonetic string for the full line
      const phonetic = wordObjects.map((wo) => wo.phonetic).join(' ')

      return {
         unspacedText: originalText,
         phonetic,
         wordObjects,
      }
   }

   /**
    * Group split separable-verb occurrences (ลอง ส่งสัญญาณ ดู → one ลองดู
    * meaning) on freshly built word objects. Conservative: the shared matcher
    * requires part2 to be clause-final, and a match is dropped unless the
    * canonical entry was found in the dictionary lookup.
    */
   private applySeparableGrouping(
      wordObjects: ApiWordObject[],
      wordDictionary: Map<string, ThaiWordLookupProps>
   ): void {
      const tokens = wordObjects.map((wo) => wo.word)
      for (const match of findSeparableMatches(tokens, separableVerbs)) {
         const canonical = wordDictionary.get(match.word)
         if (!canonical) continue
         groupSeparableTokens(
            wordObjects,
            {
               word: canonical.word,
               t2e: canonical.t2e,
               meanings:
                  canonical.meanings?.map((m) => ({
                     meaning: m.meaning || m.explanation || '',
                  })) || [],
            },
            match.indexes
         )
      }
   }

   /**
    * Translate Thai text using the CF Worker for word spacing and English translations,
    * then enrich with word-level data from dictionary lookups for mouseovers/transliteration.
    */
   async translateWithWorker(queries: string[]): Promise<WorkerTranslationResult[]> {
      if (!queries.length) return []

      // A line with no data at all: blank input, or a line the worker gave up
      // on (translationFailed distinguishes the two).
      const bareResult = (query: string, translationFailed: boolean): WorkerTranslationResult => ({
         englishTranslation: '',
         wordTranslation: { unspacedText: query, phonetic: '', wordObjects: [] },
         translationFailed,
      })

      // Step 1: Call the worker with the non-blank lines. The direction is
      // decided once here, over the whole query, and sent explicitly — the
      // worker obeys it rather than re-detecting. Blank lines never leave the
      // client; their positions are reassembled at the end.
      const direction = directionKeyFor(detectDominantLanguage(queries.join('\n')))
      const lines = queries.filter((query) => query.trim())
      if (!lines.length) {
         return queries.map((query) => bareResult(query, false))
      }
      let workerResult: WorkerCallResult
      try {
         workerResult = await this.callWorker(lines, direction)
      } catch (err) {
         // For a Thai query the local splitter can still deliver everything
         // but the English line; an English query has nothing to split, so
         // its failure surfaces as before.
         if (direction !== 'th-en') throw err
         return await this.splitWordsFallback(queries, lines, err)
      }
      const { entries, model, thaiWordLookups } = workerResult

      // Step 2: Re-merge the LLM's over-split spacing so vetted compounds land
      // on one token (พวก เขา → พวกเขา) before anything downstream reads them.
      // The worker now merges on the way out too, and the operation is
      // idempotent, so this is a no-op safety net for the release overlap —
      // remove it once no deployed worker can send unmerged spacing.
      const spacedByEntry = entries.map((entry) =>
         entry.error ? '' : mergeCompounds(entry.spaced)
      )

      // Step 3: The dictionary data for every token. The worker sends it with
      // the translation when it could reach the dictionary, which is what makes
      // a translation one request; otherwise fetch it here as before.
      const wordDictionary = thaiWordLookups
         ? new Map(thaiWordLookups.map((lookup) => [lookup.word, lookup]))
         : await this.lookupThaiWords(collectLookupTokens(spacedByEntry))

      // Step 4: Build WordTranslation objects with dictionary data
      let entryIndex = -1
      return queries.map((originalQuery): WorkerTranslationResult => {
         if (!originalQuery.trim()) {
            return bareResult(originalQuery, false)
         }
         entryIndex += 1
         const entry = entries[entryIndex]
         const spacedLine = spacedByEntry[entryIndex]
         if (!entry || entry.error || !spacedLine) {
            return bareResult(originalQuery, true)
         }

         const wordTranslation = this.buildWordTranslation(
            originalQuery,
            spacedLine,
            wordDictionary,
            entry.natural
         )
         this.applySeparableGrouping(wordTranslation.wordObjects, wordDictionary)

         return {
            englishTranslation: entry.english,
            wordTranslation,
            translationFailed: false,
            model,
         }
      })
   }

   /**
    * When the translate worker can't answer — rate limited, gated, down, or
    * timed out — segment the Thai locally with the non-LLM splitter and look
    * the words up in the dictionary worker (a separate service with its own
    * limits). The result renders like a normal translation, minus the English
    * line, and is marked translationUnavailable so the UI can say why.
    */
   private async splitWordsFallback(
      queries: string[],
      lines: string[],
      translateError: unknown
   ): Promise<WorkerTranslationResult[]> {
      let spacedByLine: string[]
      let wordDictionary: Map<string, ThaiWordLookupProps>
      try {
         // Deferred import: splitWords carries a ~4 MB bundled lexicon, so it
         // loads as its own chunk the first time a translation actually fails.
         const { splitWords } = await import('../nlp/word-split/split-words')
         spacedByLine = lines.map((line) => mergeCompounds(splitWords(line)))
         const tokens = collectLookupTokens(spacedByLine)
         wordDictionary = await this.lookupThaiWords(tokens)
         // splitWords emits mostly dictionary words, so a lookup that matched
         // none of them means the dictionary is unreachable too. With both
         // services down the fallback has nothing to add — report the
         // translation failure the page already knows how to show.
         if (wordDictionary.size === 0 && tokens.length > 0) throw translateError
      } catch {
         throw translateError
      }

      let lineIndex = -1
      return queries.map((originalQuery): WorkerTranslationResult => {
         if (!originalQuery.trim()) {
            return {
               englishTranslation: '',
               wordTranslation: { unspacedText: originalQuery, phonetic: '', wordObjects: [] },
               translationFailed: false,
            }
         }
         lineIndex += 1
         const wordTranslation = this.buildWordTranslation(
            originalQuery,
            spacedByLine[lineIndex],
            wordDictionary
         )
         this.applySeparableGrouping(wordTranslation.wordObjects, wordDictionary)
         return {
            englishTranslation: '',
            wordTranslation,
            translationFailed: false,
            translationUnavailable: true,
         }
      })
   }

   /**
    * Build WordTranslations from lookup data already delivered by another
    * response (e.g. thaiWordLookups embedded in the search result), so no
    * extra dictionary request is needed.
    */
   buildTranslationsFromLookups(
      queries: string[],
      spacedQueries: string[],
      lookups: ThaiWordLookupProps[],
      // Per-query token→sense maps (aligned by index), e.g. from each example
      // sentence's senseByWord, to narrow tooltips to the illustrated sense.
      senseByWordList?: (Record<string, number> | undefined)[]
   ): WordTranslation[] {
      const wordDictionary = new Map(lookups.map((lookup) => [lookup.word, lookup]))
      return queries.map((q, i) =>
         this.buildWordTranslation(
            q,
            spacedQueries[i] || q,
            wordDictionary,
            undefined,
            senseByWordList?.[i]
         )
      )
   }

   /**
    * Get word-level translations from the dictionary for individual word lookups.
    * Used by dictionary/search pages for example sentences that need word-level data.
    */
   async getTranslations(
      queries: string[],
      spacedQueries?: string[],
      // Per-query token→sense maps (aligned by index) to narrow tooltips to the
      // illustrated sense. Only meaningful alongside spacedQueries.
      senseByWordList?: (Record<string, number> | undefined)[]
   ): Promise<WordTranslation[]> {
      if (!queries.length) return []

      if (spacedQueries) {
         // Re-merge over-split compounds before lookup so a compound resolves
         // to one token (matches the live-translation path).
         const mergedQueries = spacedQueries.map(mergeCompounds)
         const allWords = mergedQueries.flatMap((q) => q.split(' ').filter((w) => w.trim()))
         const wordDictionary = await this.lookupThaiWords(allWords)
         return queries.map((q, i) =>
            this.buildWordTranslation(
               q,
               mergedQueries[i] || q,
               wordDictionary,
               undefined,
               senseByWordList?.[i]
            )
         )
      }

      // Partition queries into phrases (contain spaces) and single words (no spaces)
      const phraseIndices: number[] = []
      const singleWordIndices: number[] = []

      queries.forEach((q, index) => {
         if (q.includes(' ') || q.trim().length === 0) {
            phraseIndices.push(index)
         } else {
            singleWordIndices.push(index)
         }
      })

      // Fetch single words from the dictionary
      let singleWordTranslations: WordTranslation[] = []
      if (singleWordIndices.length > 0) {
         const singleWordQueries = singleWordIndices.map((i) => queries[i])
         const wordDictionary = await this.lookupThaiWords(singleWordQueries)
         singleWordTranslations = singleWordQueries.map((q) =>
            this.buildWordTranslation(q, q, wordDictionary)
         )
      }

      // Fetch phrases using the translation worker (LLM). A phrase can hold
      // several sentences, and the worker translates and caches per sentence —
      // so split each phrase into sentence-lines first and regroup the results,
      // exactly as the live-translation path does.
      let phraseTranslations: WordTranslation[] = []
      if (phraseIndices.length > 0) {
         const phraseQueries = phraseIndices.map((i) => queries[i])
         const sentenceGroups = phraseQueries.map((q) => splitQueryIntoLines(q))
         const workerResults = await this.translateWithWorker(sentenceGroups.flat())
         let cursor = 0
         phraseTranslations = sentenceGroups.map((group, groupIndex) => {
            const results = workerResults.slice(cursor, cursor + group.length)
            cursor += group.length
            return this.combinePhraseTranslation(phraseQueries[groupIndex], results)
         })
      }

      // Reconstruct final array in original order
      const results: WordTranslation[] = new Array(queries.length)
      let singleCursor = 0
      let phraseCursor = 0

      queries.forEach((_, index) => {
         if (queries[index].includes(' ') || queries[index].trim().length === 0) {
            results[index] = phraseTranslations[phraseCursor]
            phraseCursor += 1
         } else {
            results[index] = singleWordTranslations[singleCursor]
            singleCursor += 1
         }
      })

      return results
   }

   /**
    * Fold a phrase's per-sentence worker results back into one WordTranslation.
    * Any failed sentence degrades the whole phrase to a bare entry — the
    * callers render example sentences, where a partially-tokenized phrase
    * would be more misleading than an untokenized one.
    */
   private combinePhraseTranslation(
      phrase: string,
      results: WorkerTranslationResult[]
   ): WordTranslation {
      if (!results.length || results.some((r) => r.translationFailed)) {
         return { unspacedText: phrase, phonetic: '', wordObjects: [] }
      }

      const wordObjects = results.flatMap((result, index) => {
         const words = result.wordTranslation.wordObjects
         if (index === results.length - 1 || words.length === 0) return words
         // A sentence boundary is a real space in the source text.
         const last = words[words.length - 1]
         return [...words.slice(0, -1), { ...last, hasTrailingSpace: true }]
      })

      return {
         unspacedText: phrase,
         phonetic: results
            .map((r) => r.wordTranslation.phonetic)
            .filter(Boolean)
            .join(' '),
         wordObjects,
      }
   }

   async fetchThaiDictionaryWord(key: number | string) {
      if (typeof key === 'string' && (key.length > 200 || !isThaiText(key))) return undefined

      try {
         const result: ThaiWordFullProps = await this.getJson(
            `/dictionary-thai/${apiVersion}/${encodeURIComponent(key.toString())}`
         )
         return result
      } catch {
         return undefined
      }
   }

   /**
    * The as-you-type tier. Light enough to call per settled keystroke, and
    * cached hard on both sides: the worker edge-caches every prefix, and the map
    * below means backspacing back through a word costs nothing at all.
    *
    * Results only — never throws. A suggest failure should quietly leave the
    * panel empty, not interrupt someone mid-word with an error.
    */
   async suggest(query: string, signal?: AbortSignal): Promise<SuggestResult> {
      const key = query.trim()
      if (!key) return emptySuggestResult()

      const cached = this.suggestCache.get(key)
      if (cached) return cached

      // Tracked rather than inferred from the result: a query that genuinely
      // matches nothing is a real answer worth caching (typing towards a long
      // word passes through plenty of them), while an abort or an outage is not.
      let failed = false
      const pending: Promise<SuggestResult> = this.getJson(
         `/suggest/${apiVersion}/${encodeURIComponent(key)}`,
         true,
         signal
      ).catch(() => {
         failed = true
         return { ...emptySuggestResult(), suggestFailed: true }
      })

      if (this.suggestCache.size >= this.SUGGEST_CACHE_MAX) {
         const oldest = this.suggestCache.keys().next().value
         if (oldest) this.suggestCache.delete(oldest)
      }
      this.suggestCache.set(key, pending)

      void pending.then(() => {
         if (failed) this.suggestCache.delete(key)
      })

      return pending
   }

   async searchDictionary(query: string): Promise<SearchResult> {
      const clipped = query.substring(0, 200)

      // Browser only, for the same reason as workerCache: under SSR a Worker
      // isolate's module scope is shared by every request it handles.
      if (isServer) return this.fetchSearch(clipped)

      const cached = this.searchCache.get(clipped)
      if (cached) return cached

      const pending = this.fetchSearch(clipped)
      if (this.searchCache.size >= this.SEARCH_CACHE_MAX) {
         const oldest = this.searchCache.keys().next().value
         if (oldest) this.searchCache.delete(oldest)
      }
      this.searchCache.set(clipped, pending)

      // An outage must not be cached: a failed search would otherwise pin the
      // "no results" state for the rest of the session.
      void pending
         .then((result) => {
            if (result.searchFailed) this.searchCache.delete(clipped)
         })
         .catch(() => this.searchCache.delete(clipped))

      return pending
   }

   private async fetchSearch(clipped: string): Promise<SearchResult> {
      const viaBinding = await viaDictionaryBinding((rpc) => rpc.searchViaBinding(clipped))
      if (viaBinding) return viaBinding

      // The search response carries the full exact match (plus lookups for its
      // Thai tokens), so neither language needs a dedicated word fetch.
      try {
         return await this.getJson(`/search/${apiVersion}/${encodeURIComponent(clipped)}`)
      } catch {
         return {
            thaiExactMatch: undefined,
            engExactMatch: undefined,
            thaiMatches: [],
            engMatches: [],
            translitMatches: [],
            searchFailed: true,
         }
      }
   }
}

const api = new Thai2EnglishApi()
export default api
